import React from 'react';
import Icon from '@mdi/react';
import { mdiCheckCircle, mdiAlertCircle, mdiInformation, mdiClose } from '@mdi/js';
import { Box, SnackbarContent, IconButton, SxProps, Typography, Stack } from '@mui/material';
import { headingFontBold, vars } from '../../../Theme';
import { useSnackbar } from 'notistack';

/** Enum for Snackbar component. */
export enum ToastSeverity {
    Success = 'success',
    Warning = 'warning',
    Error = 'error',
    Info = 'info',
}

/**Icons for Snackbar component. */
const variantIcon: Record<ToastSeverity, string> = {
    [ToastSeverity.Success]: mdiCheckCircle,
    [ToastSeverity.Warning]: mdiAlertCircle,
    [ToastSeverity.Error]: mdiAlertCircle,
    [ToastSeverity.Info]: mdiInformation,
};

/** Colors for Snackbar icon color. */
const colors: Record<ToastSeverity, string> = {
    [ToastSeverity.Success]: vars.selectedHoveredGreen,
    [ToastSeverity.Warning]: vars.darkOrange,
    [ToastSeverity.Error]: vars.errorDialogRed,
    [ToastSeverity.Info]: vars.brandDarkBlue,
};

/** Colors for Snackbar action button icon color. */
const actionButtonColors: Record<ToastSeverity, string> = {
    [ToastSeverity.Success]: vars.selectedHoveredGreen,
    [ToastSeverity.Warning]: vars.darkOrange,
    [ToastSeverity.Error]: vars.errorDialogRed,
    [ToastSeverity.Info]: vars.brandDarkBlue,
};

/** Styles for SnackbarContent component */
const snackbarContentSx: SxProps = {
    backgroundColor: vars.white,

    '& .MuiSnackbarContent-message': {
        py: 0,
    },
    '&.success': {
        color: vars.selectedHoveredGreen,
        border: `1px solid ${vars.selectedHoveredGreen}`,
    },
    '&.warning': {
        color: vars.darkOrange,
        border: `1px solid ${vars.darkOrange}`,
    },
    '&.error': {
        color: vars.errorDialogRed,
        border: `1px solid ${vars.errorDialogRed}`,
    },
    '&.info': {
        color: vars.brandDarkBlue,
        border: `1px solid ${vars.brandDarkBlue}`,
    },
};

/** Styles for Box component */
const boxSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
};

/** Styles for message */
const messageSx: SxProps = {
    ...headingFontBold,
    fontSize: 16,
    ml: '0.25rem',
};

/** Styles for additional info */
const additionalInfoSx: SxProps = {
    ...headingFontBold,
    fontSize: 14,
    ml: '2.5rem',
};

/**
 * Snackbar Component that allows to display notifications
 */
export default React.forwardRef<HTMLDivElement, ISnackbarProps>(
    ({ id, severity, message, additionalInfo }, ref) => {
        /** Allow to close the component */
        const { closeSnackbar } = useSnackbar();

        /**
         *  Close the component
         */
        const handleCloseSnackbar = () => {
            closeSnackbar(id);
        };

        return (
            <SnackbarContent
                ref={ref}
                sx={snackbarContentSx}
                className={severity}
                message={
                    <Stack>
                        <Box sx={boxSx}>
                            {variantIcon[severity] && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Icon
                                        color={colors[severity]}
                                        path={variantIcon[severity]}
                                        size={1}
                                    />
                                </Box>
                            )}
                            <Typography sx={messageSx}>{message}</Typography>
                        </Box>
                        {additionalInfo && (
                            <Typography sx={additionalInfoSx}>{additionalInfo}</Typography>
                        )}
                    </Stack>
                }
                action={
                    <IconButton size='small' onClick={handleCloseSnackbar}>
                        <Icon color={actionButtonColors[severity]} path={mdiClose} size={1} />
                    </IconButton>
                }
            />
        );
    },
);

/**
 * Interface that describes Snackbar's props.
 */
export interface ISnackbarProps {
    /** Id of the Snackbar component */
    id: number | string;

    /** Type of the Snackbar component
     * @example 'success', 'error', 'info'
     */
    severity?: ToastSeverity;

    /** Main message in the Snackbar component */
    message: string | React.ReactNode;

    /** Optianal info in the Snackbar component */
    additionalInfo?: string;
}
